import { useState, useCallback, useRef } from 'react';
import {
  Grid,
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  CheckCircle,
  Error as ErrorIcon,
  PlayArrow,
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';

// const BASE_API_URL = 'https://api.dev.hatproject.org';
const BASE_API_URL = 'https://api.hatproject.org';

const SessionUpload = ({ isTokenExpired }) => {
  const userId = sessionStorage.getItem('logged_in_user') || '';
  const [tutorId, setTutorId] = useState('');
  const [description, setDescription] = useState('');
  const [clientId, setClientId] = useState('new_schools');
  const [uploadSource] = useState('researcher-dashboard');
  const [file, setFile] = useState(null);
  const [duration, setDuration] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [generatedTitle, setGeneratedTitle] = useState('');
  const [sessionMetadata, setSessionMetadata] = useState(null);
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState(0);
  const [sessions, setSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const theme = createTheme({
    typography: {
      fontFamily: '"Mulish",sans-serif',
    },
  });

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const isTranscriptFile = (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    return ext === 'vtt' || ext === 'webvtt' || ext === 'srt';
  };

  const parseSrtTimestamps = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target.result;
          const lines = content.split('\n');
          let firstTimestamp = null;
          let lastTimestamp = null;
          // Regex to match SRT timestamp lines: HH:MM:SS,mmm --> HH:MM:SS,mmm
          const timestampRegex = /(\d{2}:\d{2}:\d{2},\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2},\d{3})/;
          for (const line of lines) {
            const match = line.match(timestampRegex);
            if (match) {
              // Convert SRT format (comma) to VTT format (dot) for parsing
              const startTime = timestampToSeconds(match[1].replace(',', '.'));
              const endTime = timestampToSeconds(match[2].replace(',', '.'));
              // Store first timestamp
              if (firstTimestamp === null) {
                firstTimestamp = startTime;
              }
              lastTimestamp = endTime;
            }
          }
          if (firstTimestamp !== null && lastTimestamp !== null) {
            const duration = lastTimestamp - firstTimestamp;
            if (duration <= 0) {
              reject(new Error('Invalid transcript: duration must be greater than 0 seconds'));
            }
            resolve({
              firstTimestamp,
              lastTimestamp,
              duration,
            });
          } else {
            reject(new Error('No valid timestamps found in transcript file'));
          }
        } catch (error) {
          reject(new Error('Failed to parse transcript file'));
        }
      };
      reader.onerror = () => {
        reject(new Error('Failed to read transcript file'));
      };
      reader.readAsText(file);
    });
  };

  const timestampToSeconds = (timestamp) => {
    // Convert "HH:MM:SS.mmm" to seconds
    // Example: "00:01:30.500" → 90.5
    const parts = timestamp.split(':');
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const seconds = parseFloat(parts[2]);
    return hours * 3600 + minutes * 60 + seconds;
  };

  const parseVttTimestamps = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target.result;
          const lines = content.split('\n');
          let firstTimestamp = null;
          let lastTimestamp = null;
          // Regex to match timestamp lines: HH:MM:SS.mmm --> HH:MM:SS.mmm
          const timestampRegex = /(\d{2}:\d{2}:\d{2}\.\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2}\.\d{3})/;
          for (const line of lines) {
            const match = line.match(timestampRegex);
            if (match) {
              const startTime = timestampToSeconds(match[1]);
              const endTime = timestampToSeconds(match[2]);
              // Store first timestamp
              if (firstTimestamp === null) {
                firstTimestamp = startTime;
              }
              // Keep updating last timestamp
              lastTimestamp = endTime;
            }
          }
          if (firstTimestamp !== null && lastTimestamp !== null) {
            const duration = lastTimestamp - firstTimestamp;
            if (duration <= 0) {
              reject(new Error('Invalid transcript: duration must be greater than 0 seconds'));
            }
            resolve({
              firstTimestamp,
              lastTimestamp,
              duration,
            });
          } else {
            reject(new Error('No valid timestamps found in transcript file'));
          }
        } catch (error) {
          reject(new Error('Failed to parse transcript file'));
        }
      };
      reader.onerror = () => {
        reject(new Error('Failed to read transcript file'));
      };
      reader.readAsText(file);
    });
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Creates HTML5 video/audio/transcript elements to validate media files
  // Tries video first, falls back to audio
  // Returns duration, type, and validation status
  const validateFile = async (file) => {
    if (isTranscriptFile(file)) {
      const ext = file.name.split('.').pop().toLowerCase();
      const validTranscriptExtensions = ['vtt', 'webvtt', 'srt'];
      if (!validTranscriptExtensions.includes(ext)) {
        throw new Error(
          `Invalid transcript format. Only VTT and SRT formats are supported. Your file has .${ext} extension.`
        );
      }
      const maxSizeBytes = 100 * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        throw new Error(`File too large. Maximum size is ${formatBytes(maxSizeBytes)}`);
      }
      if (file.size === 0) {
        throw new Error('File is empty');
      }
      try {
        let timestamps;
        if (ext === 'srt') {
          timestamps = await parseSrtTimestamps(file);
        } else {
          // VTT or WEBVTT
          timestamps = await parseVttTimestamps(file);
        }
        return {
          isValid: true,
          duration: timestamps.duration,
          type: 'transcript',
          actualMimeType: file.type,
          transcriptData: timestamps,
        };
      } catch (error) {
        throw new Error(`Invalid transcript file: ${error.message}`);
      }
    }
    const maxSizeBytes = 100 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      throw new Error(`File too large. Maximum size is ${formatBytes(maxSizeBytes)}`);
    }
    if (file.size === 0) {
      throw new Error('File is empty');
    }
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error('File validation timeout - file may be corrupted or invalid'));
      }, 15000);
      const cleanup = () => {
        clearTimeout(timeoutId);
        URL.revokeObjectURL(url);
      };
      const tryVideo = () => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        const handleVideoLoad = () => {
          if (video.duration && video.duration > 0 && isFinite(video.duration)) {
            cleanup();
            resolve({
              isValid: true,
              duration: video.duration,
              type: 'video',
              actualMimeType: file.type,
            });
          } else {
            tryAudio();
          }
        };
        const handleVideoError = () => {
          tryAudio();
        };
        video.addEventListener('loadedmetadata', handleVideoLoad, {
          once: true,
        });
        video.addEventListener('error', handleVideoError, { once: true });
        video.addEventListener('abort', handleVideoError, { once: true });
        video.src = url;
      };
      const tryAudio = () => {
        const audio = document.createElement('audio');
        audio.preload = 'metadata';
        const handleAudioLoad = () => {
          if (audio.duration && audio.duration > 0 && isFinite(audio.duration)) {
            cleanup();
            resolve({
              isValid: true,
              duration: audio.duration,
              type: 'audio',
              actualMimeType: file.type,
            });
          } else {
            cleanup();
            reject(new Error('File appears to be corrupted or is not a valid media file'));
          }
        };
        const handleAudioError = (e) => {
          cleanup();
          reject(new Error('File is not a valid audio or video format'));
        };
        audio.addEventListener('loadedmetadata', handleAudioLoad, {
          once: true,
        });
        audio.addEventListener('error', handleAudioError, { once: true });
        audio.addEventListener('abort', handleAudioError, { once: true });
        audio.src = url;
      };
      tryVideo();
    });
  };

  const handleFile = async (selectedFile) => {
    if (!tutorId.trim()) {
      setWarning('Please provide Tutor/Teacher ID and Description to enable uploading');
      setError('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    if (!validateEmail(tutorId.trim())) {
      setWarning('Please enter a valid email address for Tutor/Teacher ID before uploading a file');
      setError('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    if (!description.trim()) {
      setWarning('⚠️ Please enter Session Description before uploading a file');
      setError('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setError('');
    setWarning('');
    setFile(null);
    setDuration(null);
    setSessionMetadata(null);
    setGeneratedTitle('');

    try {
      const validationResult = await validateFile(selectedFile);
      setFile(selectedFile);
      setDuration(validationResult.duration);
      if (description.trim() && tutorId.trim()) {
        const metadata = generateSessionMetadata(
          selectedFile,
          description,
          tutorId,
          clientId,
          validationResult.duration
        );
        setSessionMetadata(metadata);
        setGeneratedTitle(metadata.title);
      }
    } catch (error) {
      console.error('File validation failed:', error);
      setError(error.message);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const generateTitle = (description) => {
    if (description.trim()) {
      return description.trim().substring(0, 40) + (description.length > 40 ? '...' : '');
    }
    return '';
  };

  const generateSessionMetadata = (file, description, tutorId, clientId, duration) => {
    const startTime = new Date(file.lastModified);
    if (!duration || duration <= 0) {
      throw new Error('Invalid duration: Duration must be greater than 0');
    }
    const endTime = new Date(startTime.getTime() + duration * 1000);
    const title = generateTitle(description);
    const now = new Date();
    const uploadTimestamp = now.getTime();
    const uploadTime = new Date(uploadTimestamp - now.getTimezoneOffset() * 60000)
      .toISOString()
      .replace(/[:.]/g, '-')
      .slice(0, -5);
    const fileNameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.'));
    const fileExtension = file.name.substring(file.name.lastIndexOf('.'));
    const newFileName = `${fileNameWithoutExt}_${uploadTime}${fileExtension}`;
    return {
      title,
      description: description.trim(),
      tutorId: tutorId.trim(),
      clientId,
      startTime: startTime.getTime(),
      endTime: endTime.getTime(),
      duration: duration,
      fileName: newFileName,
      fileSize: file.size,
      fileType: file.type,
      uploadTimestamp: uploadTimestamp,
    };
  };

  const updateMetadata = () => {
    if (file && description.trim() && tutorId.trim() && duration && duration > 0) {
      const metadata = generateSessionMetadata(file, description, tutorId, clientId, duration);
      setSessionMetadata(metadata);
      setGeneratedTitle(metadata.title);
    } else {
      setSessionMetadata(null);
      setGeneratedTitle('');
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      const droppedFiles = Array.from(e.dataTransfer.files);
      if (droppedFiles.length > 0) {
        handleFile(droppedFiles[0]);
      }
    },
    [description, tutorId, clientId]
  );

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      handleFile(selectedFiles[0]);
    }
  };

  const removeFile = () => {
    setFile(null);
    setDuration(null);
    setSessionMetadata(null);
    setGeneratedTitle('');
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleTutorIdChange = (e) => {
    setTutorId(e.target.value);
    setTimeout(updateMetadata, 0);
  };

  const handleDescriptionChange = (e) => {
    const newDescription = e.target.value;
    if (newDescription.length <= 256) {
      setDescription(newDescription);
      setTimeout(updateMetadata, 0);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);

    if (newValue === 1) {
      loadSessions();
    }
  };

  const validateForm = () => {
    const errors = [];
    if (!tutorId.trim()) {
      errors.push('Tutor/Teacher ID is required');
    } else if (!validateEmail(tutorId.trim())) {
      errors.push('Tutor/Teacher ID must be a valid email address');
    }
    if (!description.trim()) {
      errors.push('Session description is required');
    }
    if (!file) {
      errors.push('Media file is required');
    }
    if (!duration || duration <= 0) {
      errors.push('File must have a valid duration');
    }
    return errors;
  };

  const uploadToS3 = async (file, sessionId, metadata) => {
    try {
      await isTokenExpired();
      const authToken = sessionStorage.getItem('access_token');
      const { clientId, startTime, endTime, tutorId, title } = metadata;
      const qs = new URLSearchParams({
        clientId,
        sessionId,
        startTime,
        endTime,
        tutorId,
        title,
      });

      console.log('getting presigned url. authToken:', authToken);
      const presignRes = await fetch(`${BASE_API_URL}/v1/mediaLocation?${qs.toString()}`, {
        headers: { Authorization: authToken },
      });
      if (!presignRes.ok) throw new Error(await presignRes.text());
      const { url } = await presignRes.json();

      //Direct Upload to S3
      const body = await file.arrayBuffer();
      const putRes = await fetch(url, {
        method: 'PUT',
        body,
        headers: {
          'x-amz-meta-start_time': startTime,
          'x-amz-meta-end_time': endTime,
          'x-amz-meta-tutor_id': tutorId,
          'x-amz-meta-title': title,
        },
      });
      if (!putRes.ok) throw new Error(`S3 upload failed: ${await putRes.text()}`);
      return { success: true };
    } catch (error) {
      console.error('S3 upload error:', error);
      throw new Error(`Upload failed: ${error.message}`);
    }
  };

  const startAnalysis = async (sessionId, clientId) => {
    try {
      await isTokenExpired();
      const authToken = sessionStorage.getItem('access_token');
      const sessionIdNoExt = sessionId.includes('.')
        ? sessionId.substring(0, sessionId.lastIndexOf('.'))
        : sessionId;
      const payload = {
        clientId: clientId,
        // processingType: "2025-spring-media",
        processingType: '2025-new-schools-media-standard',
        sessionId: sessionIdNoExt,
        useExisting: false,
        requestURL: '',
        reasonCode: 'new',
      };

      const analysisResponse = await fetch(`${BASE_API_URL}/v1/analyze`, {
        method: 'POST',
        headers: {
          Authorization: authToken,
        },
        body: JSON.stringify(payload),
      });
      if (!analysisResponse.ok) {
        throw new Error(`HTTP error! status: ${analysisResponse.status}`);
      }
      const data = await analysisResponse.json();
      console.log('Analysis started successfully:', data);
      let executionId = '';
      if (typeof data === 'string') {
        const match = data.match(/Execution id is ([a-f0-9-]+)/i);
        if (match) {
          executionId = match[1];
        }
      }
      console.log('Extracted execution ID:', executionId);
      return { success: true, data: data, executionId: executionId };
    } catch (error) {
      console.error('Analysis start error:', error);
      throw new Error(`Analysis failed to start: ${error.message}`);
    }
  };

  const handleUpload = async () => {
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      return;
    }
    if (!sessionMetadata) return;

    setIsUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      const sessionId = sessionMetadata.fileName;
      const uploadResult = await uploadToS3(file, sessionId, sessionMetadata);

      if (uploadResult.success) {
        setUploadProgress(100);
        try {
          const analysisResult = await startAnalysis(sessionId, clientId);

          if (analysisResult.success) {
            try {
              const sessionIdNoExt = sessionId.includes('.')
                ? sessionId.substring(0, sessionId.lastIndexOf('.'))
                : sessionId;

              await postAnalysisStatus(sessionIdNoExt, analysisResult.executionId || '');
            } catch (statusError) {
              console.error('Failed to post analysis status:', statusError);
            }

            alert(
              `Upload complete! Session "${sessionMetadata.title}" has been uploaded and analysis has started.`
            );
          } else {
            alert(
              `Upload complete! Session "${sessionMetadata.title}" has been uploaded successfully, but analysis failed to start.`
            );
          }
        } catch (analysisError) {
          console.error('Analysis error:', analysisError);
          alert(
            `Upload complete! Session "${sessionMetadata.title}" has been uploaded successfully, but analysis failed to start: ${analysisError.message}`
          );
        }

        resetForm();
      } else {
        throw new Error('File upload failed');
      }
    } catch (error) {
      console.error('Upload process failed:', error);
      setError(error.message || 'Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const postAnalysisStatus = async (sessionId, executionId) => {
    try {
      await isTokenExpired();
      const authToken = sessionStorage.getItem('access_token');
      const payload = {
        sessionId: sessionId,
        userId: userId,
        tutorId: tutorId,
        clientId: clientId,
        description: description,
        title: sessionMetadata.title,
        executionId: executionId,
        uploadTime: sessionMetadata.uploadTimestamp,
        uploadSource: uploadSource
      };

      const response = await fetch(`${BASE_API_URL}/v1aux/mediaAnalysisStatus`, {
        method: 'POST',
        headers: {
          Authorization: authToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to post analysis status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Post analysis status error:', error);
      throw error;
    }
  };

  const fetchSessions = async () => {
    try {
      await isTokenExpired();
      const authToken = sessionStorage.getItem('access_token');
      const response = await fetch(
        `${BASE_API_URL}/v1aux/mediaAnalysisStatus?userId=${encodeURIComponent(userId)}`,
        {
          method: 'GET',
          headers: {
            Authorization: authToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch sessions: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Fetch sessions error:', error);
      throw error;
    }
  };

  const loadSessions = async () => {
    if (!userId) {
      setStatusError('No logged-in user found. Please log in again.');
      return;
    }

    setIsLoadingSessions(true);
    setStatusError('');

    try {
      const data = await fetchSessions();
      const sessionsArray = Array.isArray(data) ? data : [];
      setSessions(sessionsArray);
      setLastUpdated(new Date());
    } catch (error) {
      setStatusError(error.message);
      setSessions([]);
    } finally {
      setIsLoadingSessions(false);
    }
  };

  const isFormValid =
    tutorId.trim() &&
    validateEmail(tutorId.trim()) &&
    description.trim() &&
    file &&
    duration &&
    duration > 0;

  const resetForm = () => {
    setTutorId('');
    setDescription('');
    setClientId('new_schools');
    setFile(null);
    setDuration(null);
    setSessionMetadata(null);
    setGeneratedTitle('');
    setError('');
    setIsUploading(false);
    setUploadProgress(0);
    setIsDragging(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ maxWidth: 1400, margin: '0 auto', padding: 3, backgroundColor: '#F9F7F6' }}>
        <Typography variant="h5" align="left" gutterBottom sx={{ mb: 4 }}>
          Media Upload
        </Typography>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
            sx={{ mb: 3, backgroundColor: 'transparent' }}

        >
          <Tab sx={{ textTransform: 'none' }} label="Upload Media" />
          <Tab sx={{ textTransform: 'none' }} label="Session Status" />
        </Tabs>
        {activeTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                type="email"
                label="Tutor/Teacher ID (Email)"
                value={tutorId}
                onChange={handleTutorIdChange}
                error={tutorId && !validateEmail(tutorId)}
                helperText={
                  tutorId && !validateEmail(tutorId) ? 'Please enter a valid email address' : ''
                }
                placeholder="teacher@example.com"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Client ID</InputLabel>
                <Select
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  label="Client ID"
                  sx={{ textAlign: 'left' }}
                >
                  <MenuItem value="new_schools">NewSchools</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                rows={3}
                variant="outlined"
                label="Session Description"
                value={description}
                onChange={handleDescriptionChange}
                placeholder="Describe this tutoring session..."
                helperText={`${description.length}/256 characters`}
                inputProps={{ maxLength: 256 }}
              />
            </Grid>
            {generatedTitle && (
              <Grid item xs={12}>
                <Alert severity="info" icon={<CheckCircle />}>
                  <Typography variant="body2">
                    <strong>Generated Title:</strong> {generatedTitle}
                  </Typography>
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="h6" align="left" gutterBottom>
                Media File *
              </Typography>
              {!file || !sessionMetadata ? (
                <Paper
                  elevation={0}
                  sx={{
                    border: `1px dashed ${isDragging ? '#1F72E7' : '#CDC8C5'}`,
                    borderRadius: 2,
                    padding: 6,
                    textAlign: 'center',
                    backgroundColor: isDragging ? '#f3f9ff' : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: '#FDFBFA',
                      borderColor: '#1F72E7',
                    },
                  }}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    Drop your media file here or click to select
                  </Typography>
                  {/* <Typography variant="body2" color="textSecondary">
                  Supported: {supportedFormats.join(', ')} • Max: {formatBytes(maxSizeBytes)}
                </Typography> */}
                  <Button variant="outlined" startIcon={<CloudUpload />} sx={{ mt: 2, color:'#1F72E7' }}>
                    Choose File
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    accept="audio/*,video/*,.vtt,.webvtt,.srt"
                    style={{ display: 'none' }}
                  />
                </Paper>
              ) : (
                <Card variant="outlined">
                  <CardContent>
                    <Grid container justifyContent="space-between" alignItems="center">
                      <Grid item xs={10}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <PlayArrow sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="h6">
                            {sessionMetadata.fileName || file.name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Chip
                            label={`Size: ${formatBytes(file.size)}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          {duration && (
                            <Chip
                              label={`Duration: ${formatDuration(duration)}`}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={2} sx={{ textAlign: 'right' }}>
                        <IconButton onClick={removeFile} color="error" size="large">
                          <Delete />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Grid>
            {error && (
              <Grid item xs={12}>
                <Alert severity="error" icon={<ErrorIcon />}>
                  {error}
                </Alert>
              </Grid>
            )}
            {warning && (
              <Grid item xs={12}>
                <Alert severity="warning" icon={<ErrorIcon />}>
                  {warning}
                </Alert>
              </Grid>
            )}
            {sessionMetadata && (
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ backgroundColor: '#f8f9fa' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                      Session Metadata Preview
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Tutor:</strong> {sessionMetadata.tutorId}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Client:</strong> {sessionMetadata.clientId}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Start Time:</strong>{' '}
                          {new Date(sessionMetadata.startTime).toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>End Time (Est.):</strong>{' '}
                          {new Date(sessionMetadata.endTime).toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Duration:</strong> {formatDuration(sessionMetadata.duration)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}
            {isUploading && (
              <Grid item xs={12}>
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Uploading...
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                      {uploadProgress}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                    sx={{ height: 8, borderRadius: 5 }}
                  />
                </Box>
              </Grid>
            )}
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleUpload}
                disabled={!isFormValid || isUploading}
                startIcon={isUploading ? <CircularProgress size={20} /> : <CloudUpload />}
                sx={{
                  py: 1,
                  fontSize: '1rem',
                  textTransform: 'none',
                  color: '#fff',
                }}
              >
                {isUploading ? 'Uploading...' : 'Upload & Analyze Session'}
              </Button>
            </Grid>
          </Grid>
        )}
        {activeTab === 1 && (
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography variant="h6">Session analysis status</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {lastUpdated && (
                  <Typography variant="body2" color="text.secondary">
                    Last updated: {lastUpdated.toLocaleString()}
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  onClick={loadSessions}
                  disabled={isLoadingSessions || !userId}
                >
                  Refresh
                </Button>
              </Box>
            </Box>

            {statusError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {statusError}
              </Alert>
            )}

            {!userId ? (
              <Alert severity="info">
                No logged-in user found. Please log in to view sessions.
              </Alert>
            ) : isLoadingSessions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : sessions.length === 0 ? (
              <Alert severity="info">No sessions found for user: {userId}</Alert>
            ) : (
              <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto', backgroundColor: 'transparent', border: '1px solid #ddd', borderRadius: '8px', boxShadow: 'none' }}>
                <Table sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 100, color: '#000', fontWeight: 600 }}>Upload Date</TableCell>
                      <TableCell sx={{ minWidth: 100, color: '#000', fontWeight: 600 }}>Client Id</TableCell>
                      <TableCell sx={{ minWidth: 150, color: '#000', fontWeight: 600 }}>Tutor Id</TableCell>
                      <TableCell sx={{ minWidth: 200, color: '#000', fontWeight: 600 }}>Session Id</TableCell>
                      <TableCell sx={{ minWidth: 220, color: '#000', fontWeight: 600 }}>Description</TableCell>
                      <TableCell sx={{ minWidth: 130, color: '#000', fontWeight: 600 }}>Processing Status</TableCell>
                      <TableCell sx={{ minWidth: 100, color: '#000', fontWeight: 600 }}>Talk Tree Link</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sessions.map((session, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {new Date(parseInt(session.uploadTime)).toLocaleDateString()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(parseInt(session.uploadTime)).toLocaleTimeString()}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{session.clientId}</TableCell>
                        <TableCell>{session.tutorId}</TableCell>
                        <TableCell>{session.sessionId}</TableCell>
                        <TableCell>{session.description}</TableCell>
                        <TableCell>
                          <Chip
                            label={session.status || 'Processing'}
                            size="small"
                            sx={{
                              backgroundColor:
                                session.status === 'completed'
                                  ? '#68B254'
                                  : session.status === 'Uploaded'
                                  ? '#93918E'
                                  : session.status?.startsWith('analyzing')
                                  ? '#93918E'
                                  : session.status?.startsWith('error')
                                  ? '#E3644D'
                                  : '#93918E',
                              color: '#fff',
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {session.link ? (
                            <a
                              href={session.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: '#1F72E7',
                                textDecoration: 'underline',
                              }}
                            >
                              Talk Tree
                            </a>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Not Yet Ready
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default SessionUpload;
